// src/utils/orgTime.js — orgtime-7c2e4b19 (BACKLOG: "Recurring end date saves a day early")
//
// WEB COPY of functions/utils/orgTime.js — read that file's header for what each function means.
// Everything between orgtime-begin and orgtime-end is byte-identical in all three copies (server,
// web, phone); only the export line differs. functions/scripts/testOrgTime.js runs every copy
// against functions/scripts/fixtures/orgTimeCases.json and compares the regions. Change one,
// change all three.
//
// Use it for DAYS the company means — an end date, a reminder day, a run date — with
// companySettings.timezone. Never for showing a visit's clock time.

// ── orgtime-begin ──────────────────────────────────────────────────────────────────────────────
const DEFAULT_ORG_TIMEZONE = 'America/Toronto';
const HALF_DAY_MS = 12 * 60 * 60 * 1000;

function isValidTimeZone(tz) {
  if (!tz || typeof tz !== 'string') return false;
  try { new Intl.DateTimeFormat('en-US', { timeZone: tz }); return true; } catch (e) { return false; }
}

function resolveOrgTimeZone(tz) {
  return isValidTimeZone(tz) ? tz : DEFAULT_ORG_TIMEZONE;
}

// A Date, a Firestore Timestamp, epoch milliseconds or an ISO string → a valid Date, else null.
function toInstant(v) {
  if (v === null || v === undefined || v === '') return null;
  const d = typeof v.toDate === 'function' ? v.toDate() : (v instanceof Date ? v : new Date(v));
  return d instanceof Date && !isNaN(d.getTime()) ? d : null;
}

function isYmd(s) {
  if (typeof s !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const [y, m, d] = s.split('-').map(Number);
  const t = new Date(Date.UTC(y, m - 1, d));
  return t.getUTCFullYear() === y && t.getUTCMonth() === m - 1 && t.getUTCDate() === d;
}

function pad2(n) { return String(n).padStart(2, '0'); }

// The wall clock in `tz` at instant `d`, as numbers.
function wallClock(d, tz) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(d);
  const get = (type) => Number((parts.find((p) => p.type === type) || {}).value);
  return { y: get('year'), m: get('month'), d: get('day'), h: get('hour'), mi: get('minute'), s: get('second') };
}

// How far `tz`'s wall clock is ahead of UTC at instant `d`, in milliseconds (Toronto in winter: −5 h).
function offsetMs(d, tz) {
  const w = wallClock(d, tz);
  const wholeSecond = d.getTime() - (((d.getTime() % 1000) + 1000) % 1000);
  return Date.UTC(w.y, w.m - 1, w.d, w.h, w.mi, w.s) - wholeSecond;
}

function ymdAddDays(ymd, n) {
  const [y, m, d] = ymd.split('-').map(Number);
  const t = new Date(Date.UTC(y, m - 1, d + n));
  return `${t.getUTCFullYear()}-${pad2(t.getUTCMonth() + 1)}-${pad2(t.getUTCDate())}`;
}

function orgYmd(instant, tz) {
  const d = toInstant(instant);
  if (!d) return null;
  const w = wallClock(d, resolveOrgTimeZone(tz));
  return `${w.y}-${pad2(w.m)}-${pad2(w.d)}`;
}

function orgDayStart(ymd, tz) {
  if (!isYmd(ymd)) return null;
  const zone = resolveOrgTimeZone(tz);
  const [y, m, d] = ymd.split('-').map(Number);
  const guess = Date.UTC(y, m - 1, d);
  const first = offsetMs(new Date(guess), zone);
  let t = guess - first;
  const second = offsetMs(new Date(t), zone);
  if (second !== first) t = guess - second; // the offset changed between the guess and the answer
  return new Date(t);
}

function orgDayEnd(ymd, tz) {
  if (!isYmd(ymd)) return null;
  return new Date(orgDayStart(ymdAddDays(ymd, 1), tz).getTime() - 1);
}

function orgEndOfDay(instant, tz) {
  const ymd = orgYmd(instant, tz);
  return ymd ? orgDayEnd(ymd, tz) : null;
}

function orgNearestYmd(instant, tz) {
  const d = toInstant(instant);
  return d ? orgYmd(new Date(d.getTime() + HALF_DAY_MS), tz) : null;
}

function orgDateYmd(instant, tz) {
  const d = toInstant(instant);
  if (!d) return null;
  return orgEndOfDay(d, tz).getTime() === d.getTime() ? orgYmd(d, tz) : orgNearestYmd(d, tz);
}

// tzhour-6a3d9c58 — the company's wall-clock time at an instant: { h, mi }.
function orgWallClock(instant, tz) {
  const d = toInstant(instant);
  if (!d) return null;
  const w = wallClock(d, resolveOrgTimeZone(tz));
  return { h: w.h, mi: w.mi };
}

// tzhour-6a3d9c58 — the instant of h:mi on company day `ymd`. A time skipped by a spring-forward
// change (2:30 AM) moves forward by the gap (3:30 AM); a time that happens twice on a fall-back day
// (1:30 AM) takes the earlier one.
function orgAtTime(ymd, h, mi, tz) {
  if (!isYmd(ymd) || !Number.isInteger(h) || !Number.isInteger(mi)) return null;
  const zone = resolveOrgTimeZone(tz);
  const [y, m, d] = ymd.split('-').map(Number);
  const guess = Date.UTC(y, m - 1, d, h, mi);
  const before = guess - offsetMs(new Date(guess - 86400000), zone);
  const after = guess - offsetMs(new Date(guess + 86400000), zone);
  const fits = (t) => { const w = wallClock(new Date(t), zone); return w.y === y && w.m === m && w.d === d && w.h === h && w.mi === mi; };
  const ok = [before, after].filter(fits);
  return new Date(ok.length ? Math.min(...ok) : before);
}
// ── orgtime-end ────────────────────────────────────────────────────────────────────────────────

export { DEFAULT_ORG_TIMEZONE, isValidTimeZone, resolveOrgTimeZone, isYmd, ymdAddDays, orgYmd, orgDayStart, orgDayEnd, orgEndOfDay, orgNearestYmd, orgDateYmd, orgWallClock, orgAtTime };
